import { IMAGES } from '@/lib/constants';
import { FadeIn } from '@/components/FadeIn';
import { useContentValue } from '@/contexts/SiteContentContext';
import { Coffee, Martini, Tv, PartyPopper, Target, Users } from 'lucide-react';

const FEATURES = [
  { icon: Coffee, label: 'Kaffee', desc: 'Erstklassiger Espresso & mehr' },
  { icon: Martini, label: 'Cocktails', desc: 'Kreative Drinks & Klassiker' },
  { icon: Tv, label: 'Live Sport', desc: 'Fussball auf Grossleinwand' },
  { icon: PartyPopper, label: 'Events', desc: 'Geburtstage & Privatanlässe' },
  { icon: Target, label: 'Darts', desc: 'Spiel & Spass' },
  { icon: Users, label: 'Community', desc: 'Dein Treffpunkt im Quartier' },
];

export default function AboutSection() {
  const c = useContentValue();
  return (
    <section id="erlebnis" data-testid="about-section" className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6">
        <FadeIn>
          <div className="text-center mb-16">
            <span className="text-amber-400 text-xs tracking-[0.3em] uppercase font-body">{c('Erlebnis.Eyebrow', 'Das Erlebnis')}</span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-stone-100 mt-3 tracking-wide uppercase">
              {c('Erlebnis.Überschrift', 'Mehr als ein Café')}
            </h2>
            <p className="text-stone-400 text-base sm:text-lg max-w-2xl mx-auto mt-4 font-body leading-relaxed">
              {c('Erlebnis.Beschreibung', 'TreffPunkt ist dein Ort zum Entspannen, Geniessen und Feiern. Ob Kaffee am Morgen, Cocktails am Abend oder Live-Fussball mit Freunden.')}
            </p>
          </div>
        </FadeIn>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-16">
          <FadeIn delay={100}>
            <div className="img-zoom rounded-2xl overflow-hidden h-[400px] lg:h-full">
              <img
                src={IMAGES.terrace}
                alt="Terrasse des TreffPunkt Café-Bar in Luzern"
                className="w-full h-full object-cover"
              />
            </div>
          </FadeIn>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <FadeIn delay={200}>
              <div className="img-zoom rounded-2xl overflow-hidden h-[200px]">
                <img
                  src={IMAGES.barCounter}
                  alt="Bartheke mit Cocktails im TreffPunkt Luzern"
                  className="w-full h-full object-cover"
                />
              </div>
            </FadeIn>
            <FadeIn delay={300}>
              <div className="img-zoom rounded-2xl overflow-hidden h-[200px]">
                <img
                  src={IMAGES.birthday}
                  alt="Geburtstagsfeier im TreffPunkt Café-Bar Luzern"
                  className="w-full h-full object-cover"
                />
              </div>
            </FadeIn>
            <FadeIn delay={400} className="sm:col-span-2">
              <div className="glass-card rounded-2xl p-8 h-full flex flex-col justify-center">
                <p className="text-stone-300 font-body leading-relaxed text-base">
                  {c('Erlebnis.Story', 'Seit Jahren ist TreffPunkt der beliebte Treffpunkt im Quartier am Gasshof 2 in Luzern. Ob für einen schnellen Espresso, einen gemütlichen Abend mit Freunden oder eine unvergessliche Geburtstagsfeier — bei uns bist du immer willkommen.')}
                </p>
              </div>
            </FadeIn>
          </div>
        </div>

        {/* Feature Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {FEATURES.map((feature, i) => (
            <FadeIn key={feature.label} delay={i * 80}>
              <div
                data-testid={`feature-${feature.label.toLowerCase()}`}
                className="glass-card rounded-xl p-5 text-center hover:border-amber-400/20 transition-all duration-300 group"
              >
                <feature.icon
                  size={24}
                  className="text-amber-400 mx-auto mb-3 group-hover:scale-110 transition-transform duration-300"
                />
                <h3 className="text-stone-100 font-body font-semibold text-sm mb-1">
                  {feature.label}
                </h3>
                <p className="text-stone-500 text-xs font-body">{feature.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}
